<template>
	<CDeprecatedDialog
		:hide-close-btn="true"
		:visible="show"
		:max-width="800"
		class="form-builder-preview-dialog"
		:class="{'form-builder-preview-dialog--hide-all': hideForms}"
		:full-scrollbar="false"
		:data-test-id="version"
		@open="openFormPreview"
		@close="handleClose"
	>
		<div ref="formMountPoint" class="form-builder-preview-dialog__container w-100">
		</div>
		<template #buttons-left>
			<div class="form-builder-preview-dialog__settings flex flex--align b6:show">
				<div>
					<label for="enable-preview-validation">
						Validation:
					</label>
					<button
						class="button--no-style form-builder-preview-dialog__validation-text"
						:class="{'form-builder-preview-dialog__validation-text--on': validationEnabled }"
						@click="toggleValidation"
					>
						{{ validationEnabled ? 'On' : 'Off' }}
					</button>
					<input id="enable-preview-validation" ref="validationCheckbox" v-model="validationEnabled" class="hide" type="checkbox" />
				</div>

				<div>
					<label>
						Status:
					</label>
					<CSubtleSelect
						:value="currentStatus"
						:options="statusOptions"
						class="form-builder-preview-dialog__status"
						prefix-component="CSwatch"
						@change="handleStatusChange"
					/>
				</div>

				<div>
					<label>
						Role:
					</label>
					<CSubtleSelect
						:value="currentRole"
						:options="roleOptions"
						class="form-builder-preview-dialog__role"
						@change="handleRoleChange"
					/>
				</div>

				<div v-if="isOnConfirmationPage">
					<button type="button" class="form-builder-preview-dialog__continue-workflow button--no-style py-1 px-2" @click="resetAction">
						<span class="flex flex--align gap-3">Continue <Inline svg="arrow-right" class="form-builder-preview-dialog__button-icon" />
						</span>
					</button>
				</div>
				<div v-else-if="hasChanges">
					<button type="button" class="form-builder-preview-dialog__reset-workflow button--no-style py-1 px-2" @click="resetToBeginning">
						<span class="flex flex--align gap-3">Reset <Inline svg="reset" class="form-builder-preview-dialog__button-icon" />
						</span>
					</button>
				</div>
			</div>
		</template>
		<template #footer>
			<button
				class="button button--primary"
				@click="handleClose"
			>
				Close
			</button>
		</template>
	</CDeprecatedDialog>
</template>

<script lang ="ts">
	import CDeprecatedDialog from 'src/components/dialogs/DeprecatedDialog.vue';
	import { linkStylesheet } from './utils/utils';
	import CSubtleSelect from 'src/components/form-controls/SubtleSelect.vue';
	import type { PropType } from 'vue';
	import { defineComponent } from 'vue';
	import type { FormEntryStatus } from '@cognitoforms/types/server-types/forms/model/form-entry-status';
	import type { WorkflowRole } from '@cognitoforms/types/server-types/forms/model/workflow-role';
	import { useAdminComponents } from './utils/admin-components';

	const publicFormCss = '/Content/public-form.css';
	let version = 0;

	export default defineComponent({
		name: 'FormBuilderPreview',
		components: {
			CDeprecatedDialog,
			CSubtleSelect
		},
		props: {
			show: { type: Boolean, default: false },
			adminApi: { type: Object, required: true },
			formId: { type: String, default: '0' },
			formJson: { type: String, required: true },
			entryStatuses: { type: Array as PropType<FormEntryStatus[]>, default: () => [] },
			roles: { type: Array as PropType<WorkflowRole[]>, default: () => [] },
			cacheKey: { type: Number, required: true }
		},
		data: vm => ({
			// PreviewState
			version,
			saveCard: false,
			serializedEntry: undefined,
			serializedOrder: undefined,
			formHandle: null,
			validationEnabled: true,
			currentStatus: 'Incomplete',
			statusFromUser: 'Incomplete',
			currentRole: vm.roles.find(r => r.IsPublic === true).Name,
			roleFromUser: '',
			hasChanges: false,
			isOnConfirmationPage: false,
			statusFromEntry: 'Incomplete',
			roleFromEntry: '',
			hideForms: false
		}),
		computed: {
			statusOptions() {
				return this.entryStatuses
					.filter(s => !s.IsArchived)
					.map(s => ({
						label: s.Name,
						value: s.Name,
						prefixProps: { color: s.Color }
					}));
			},
			roleOptions() {
				return this.roles.map(r => ({ value: r.Name, label: r.Name }));
			},
			defaultRole() {
				return this.roles.find(r => r.IsPublic === true).Name;
			}
		},
		watch: {
			statusOptions(newObj, oldObj) {
				const newValue = newObj.map(s => s.value);
				const oldValue = oldObj.map(s => s.value);

				if (newValue.length === oldValue.length && !newValue.includes(this.statusFromUser))
					this.statusFromUser = newValue[oldValue.indexOf(this.statusFromUser)];

				if (newValue.length === oldValue.length && !newValue.includes(this.currentStatus))
					this.currentStatus = newValue[oldValue.indexOf(this.currentStatus)];
			},
			roleOptions(newObj, oldObj) {
				const newValue = newObj.map(s => s.value);
				const oldValue = oldObj.map(s => s.value);

				if (newValue && oldValue) {
					if (this.roleFromUser) {
						if (newValue.length === oldValue.length && !newValue.includes(this.roleFromUser))
							this.roleFromUser = newValue[oldValue.indexOf(this.roleFromUser)];
						else if (!newValue.includes(this.roleFromUser))
							this.roleFromUser = newValue[0];
					}

					if (newValue.length === oldValue.length && !newValue.includes(this.currentRole))
						this.currentRole = newValue[oldValue.indexOf(this.currentRole)];
					else if (!newValue.includes(this.currentRole))
						this.currentRole = newValue[0];
				}
			},
			statusFromEntry() {
				if (this.serializedEntry !== undefined)
					this.hasChanges = true;
				this.currentStatus = this.statusFromEntry;
			},
			roleFromEntry() {
				if (this.serializedEntry !== undefined && this.currentRole !== this.defaultRole)
					this.hasChanges = true;
				this.currentRole = this.roleFromEntry;
			},
			defaultRole(value) {
				this.currentRole = value;
				this.formHandle?.setRole(this.currentRole);
			},
			cacheKey() {
				this.resetState();
			}
		},
		async mounted() {
			useAdminComponents();
		},

		methods: {
			async openFormPreview() {
				// Add stylesheet for preview if necessary
				const linkElement = document.querySelector('link[href="/Content/public-form.css"]');
				if (!linkElement)
					linkStylesheet(publicFormCss);

				const mountElement = (this.$refs.formMountPoint as HTMLElement).appendChild(document.createElement('div'));
				this.formHandle = this.adminApi.mount(this.formId, mountElement, 'preview', this.formJson);

				this.formHandle
					.on('afterSubmit', e => {
						this.isOnConfirmationPage = true;
						this.statusFromEntry = e.data.entry.Entry.Status;
						this.roleFromEntry = e.data.entry.Entry.Role;
					})
					.on('formPreviewUpdated', e => {
						this.hasChanges = true;
					});

				const { entryStatus, entryRole } = await this.formHandle.intializePreview({
					serializedEntry: this.serializedEntry,
					serializedOrder: this.serializedOrder,
					saveCard: this.saveCard,
					role: this.currentRole,
					status: this.currentStatus
				});

				// the entry may have resolved a replacement status during initialization
				this.statusFromEntry = entryStatus;
				this.roleFromEntry = entryRole;
				if (this.formHandle)
					this.formHandle.enforceValidation(this.validationEnabled);
			},
			async handleClose() {
				if (this.hasChanges) {
					const { serializedEntry, serializedOrder, saveCard } = await this.formHandle.getEntryData(this.formId);
					this.serializedEntry = serializedEntry;
					this.serializedOrder = serializedOrder;
					this.saveCard = saveCard;
				}
				this.isOnConfirmationPage = false;

				if (this.formHandle) {
					await this.formHandle?.formMounted;
					this.formHandle?.destroy();
					this.formHandle = null;
				}

				this.$emit('close');
			},
			toggleValidation() {
				this.validationEnabled = !this.validationEnabled;
				this.formHandle.enforceValidation(this.validationEnabled);
			},
			handleStatusChange(val) {
				if (val === this.statusOptions[0].value)
					this.statusFromUser = '';
				else
					this.statusFromUser = val;

				this.currentStatus = val;

				this.formHandle.setEntryStatus(val);
			},
			async handleRoleChange(val) {
				if (val === this.roleOptions[0].value)
					this.roleFromUser = '';
				else
					this.roleFromUser = val;

				this.currentRole = val;
				if (this.isOnConfirmationPage)
					await this.temporarilyHideForm();
				await this.formHandle.setRole(val);
				this.isOnConfirmationPage = false;
			},
			async resetAction() {
				if (this.currentStatus === this.statusOptions[0].value)
					this.statusFromUser = '';
				else
					this.statusFromUser = this.currentStatus;

				if (this.currentRole === this.roleOptions[0].value)
					this.roleFromUser = '';
				else
					this.roleFromUser = this.currentRole;
				this.isOnConfirmationPage = false;

				// The confirmation page is meant to be a terminal state so hide the form during the switch back to a "new form"
				await this.temporarilyHideForm();
				await this.formHandle.returnToStartingPage();
				this.version = ++version;
			},
			async temporarilyHideForm() {
				this.hideForms = true;
				await this.$nextTick();
				const confirmationTransitionDuration = 400;
				setTimeout(() => {
					this.hideForms = false;
				}, confirmationTransitionDuration);
			},
			async resetToBeginning() {
				const formHandle = this.formHandle;
				const validationEnabled = this.validationEnabled;
				this.resetState();
				this.validationEnabled = validationEnabled;
				this.formHandle = formHandle;
				this.currentRole = this.roleFromEntry = this.defaultRole;
				await this.formHandle.startNewEntry(this.defaultRole);
				this.version = ++version;
			},
			resetField(path) {
				if (this.serializedEntry === undefined)
					return;

				this.removeProperty(path.split('.'), this.serializedEntry);

				this.serializedEntry.Id = null;
			},
			removeProperty(pathSteps: string[], targetObj) {
				let currentTarget = targetObj;
				for (let i = 0; i < pathSteps.length; i++) {
					const propertyName = pathSteps[i];
					if (currentTarget.length) {
						// walk the remaining path for each member of the repeating section
						for (let j = 0; j < currentTarget.length; j++) {
							this.removeProperty(pathSteps.slice(i), currentTarget[j]);
						}
					}
					else {
						if (pathSteps.length - 1 === i) {
							delete currentTarget[propertyName];
						}
						else {
							currentTarget = currentTarget[propertyName];
						}
					}
				}
			},
			resetState() {
				Object.assign(this.$data, this.$options.data.apply(this));
			}
		}
	});
</script>

<style lang="scss">
@import '@cognitoforms/styles/app/common/faux-chameleon';
@import '@cognitoforms/styles/global/motion';

.form-builder-preview-dialog {

	&--hide-all &__container {
		opacity: 0 !important;
	}

	.cog-page.cog-fade-leave-active,
	.cog-page.visible-leave-active {
		opacity: 0 !important;
	}

	.c-modal-content {
		display: flex;
		flex-grow: 1;
		overflow: visible;
		padding: 0;

		&-container {
			display: flex;
			flex-direction: column;
		}
	}

	.c-modal-title-bar {
		display: none;
	}

	.c-modal {
		height: 100%;

		/* stylelint-disable selector-max-specificity */
		:root:root:root:root:root:root & {

			.cog-loader {
				display: none !important;
			}

			.cog-form {
				display: flex;
				align-items: stretch;
				box-shadow: none;
			}

			.cog-form__container {
				flex: 1;

				// Contrary to the public form, the form should always stretch to bottom of iframe, regardless of vertical snapping status
				min-height: 100%;
				box-shadow: none;

				// Avoid scrollbar jitter when submitting form due to the old/new page being stacked on top of each other, invisible
				.cog-confirmation + .cog-page-leave-to {
					display: none;
				}

				.el-dialog {
					margin-top: 15vh !important;
				}
			}
		}
		/* stylelint-enable selector-max-specificity */
	}

	&__validation-text {
		min-width: 15px;
		border-bottom: 1px dotted var(--shade-8);

		&:focus {
			border-bottom: 1px solid var(--outline-focused);
		}
	}

	&__settings {
		text-align: left;
		white-space: nowrap;

		.subtle-select {
			max-width: 100px;
		}

		label {
			font-weight: $weight__text--bold;
		}

		& > div {
			display: flex;
			align-items: center;
			gap: $space__2;

			& + div {
				margin-left: $space__4;
			}

			& > * {
				vertical-align: middle;
			}
		}
	}

	&__reset-workflow,
	&__continue-workflow {
		background: var(--shade-3);
		border: 1px solid var(--shade-4);

		&:hover {
			background: transparent;
		}
	}

	&__button-icon {
		margin-left: $space__1;
		color: var(--icon-subtle);
	}

	.c-modal-button-bar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		background: var(--shade-2);
		border-top: 1px solid var(--border-primary);
		padding: $space__3 $modal-margins;

		.c-modal-buttons-left,
		.c-modal-buttons-left > div {
			height: 100%;
			margin-right: $space__2;
		}

		@include b-down($b7) {
			padding: $space__3;

			.button {
				min-width: 70px;
			}
		}
	}
}

</style>